import * as React from 'react';

import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import { toWorkbenchActions, type ShellAction } from './action-definition';
import { PageHeader } from './page-header';

/**
 * One operator action available from the workbench command bar. Workbenches
 * expose at minimum the create/edit/revise/approve/close set the regulated
 * operations need — pass the actions that apply to the current entity state
 * (the consuming view is responsible for filtering by permission + lifecycle).
 */
export interface WorkbenchAction {
  /** Stable id for the button (also the key). */
  id: string;
  /** Visible label. Keep short — fits in a command-bar pill. */
  label: string;
  /** Optional left icon. */
  icon?: React.ReactNode;
  /** Click handler. */
  onClick: () => void;
  /** Visual emphasis. `primary` for the main action; `destructive` for irreversible. */
  variant?: 'default' | 'ghost' | 'outline' | 'destructive' | 'secondary';
  /** Disable the action and show a tooltip. */
  disabled?: boolean;
  /** Tooltip / a11y description. */
  hint?: string;
  /** Render a busy spinner when true. */
  loading?: boolean;
}

export interface WorkbenchShellProps {
  /** Title, breadcrumb-friendly. */
  title: string;
  /** Optional subtitle (entity number, version, status pill, etc). */
  subtitle?: React.ReactNode;
  /**
   * Actions displayed in the right rail of the command bar. Accepts the
   * canonical `ActionDefinition` (preferred for new code) or the legacy
   * `WorkbenchAction` shape — lists can mix both during migration.
   */
  actions?: ShellAction[];
  /** Secondary nav rendered above the body — usually `<Tabs>`. */
  nav?: React.ReactNode;
  /** Right-hand drawer/panel (audit, comments). Renders inline alongside body. */
  side?: React.ReactNode;
  /**
   * How the side rail behaves on small (below-lg) viewports. On lg+ the rail is
   * always a sticky right column.
   *   - `stack` (default): rail stacks below the body — the long-standing
   *     behaviour. Fine for short rails, but a tall audit/comments rail pushes
   *     the body far down on mobile.
   *   - `sticky`: the rail floats as a sticky block above the stacked body so an
   *     operator can scroll the body while the rail's top stays in view.
   *   - `fixed`: the rail is pinned to the bottom of the viewport as a compact
   *     drawer (respecting the notch safe-area) and the body reserves space for
   *     it, so the rail never pushes the body down.
   */
  sidePosition?: 'stack' | 'sticky' | 'fixed';
  /** Workbench body. */
  children: React.ReactNode;
  /** Optional class. */
  className?: string;
}

/**
 * Standard read-mostly → workbench conversion shell.
 *
 * Wraps a view in:
 *   - a PageHeader with title + subtitle
 *   - a command-bar of operator actions (create/edit/revise/approve/close)
 *   - optional tab nav strip
 *   - body + optional side rail (use AuditDrawer or CommentsAndAttachmentsPanel)
 *
 * The goal is to retire "read-mostly" views: every operational surface
 * gets the same affordances so operators don't have to context-switch
 * to a separate form module to take action.
 *
 * @example
 * <WorkbenchShell
 *   title="Batch record"
 *   subtitle={<>BR-{r.batchNumber} · <Badge>{r.status}</Badge></>}
 *   actions={[
 *     { id:'sign', label:'Sign', icon:<Edit3/>, onClick:openSign, variant:'default' },
 *     { id:'reject', label:'Reject', onClick:openReject, variant:'destructive' },
 *   ]}
 *   side={<CommentsAndAttachmentsPanel ... />}
 * >
 *   <BatchDetail record={r} />
 * </WorkbenchShell>
 */
export function WorkbenchShell({
  title,
  subtitle,
  actions = [],
  nav,
  side,
  sidePosition = 'stack',
  children,
  className,
}: WorkbenchShellProps) {
  // Normalize ActionDefinition entries (intent → variant, onSelect → onClick,
  // hidden filtered) so the render path below deals with one shape only.
  const resolvedActions = React.useMemo(() => toWorkbenchActions(actions), [actions]);
  return (
    <div className={cn('flex flex-col gap-4 p-4', className)} data-testid="workbench-shell">
      <PageHeader
        title={title}
        subtitle={typeof subtitle === 'string' ? subtitle : undefined}
        meta={typeof subtitle !== 'string' ? subtitle : undefined}
      />

      {resolvedActions.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-surface px-3 py-2"
          role="toolbar"
          aria-label="Workbench actions"
        >
          {resolvedActions.map((a) => (
            <Button
              key={a.id}
              variant={a.variant ?? 'outline'}
              size="sm"
              onClick={a.onClick}
              disabled={a.disabled || a.loading}
              title={a.hint}
              aria-label={a.hint ?? a.label}
            >
              {a.icon && <span className="mr-1 inline-flex">{a.icon}</span>}
              {a.loading ? 'Working…' : a.label}
            </Button>
          ))}
        </div>
      )}

      {nav && (
        <div className="overflow-x-auto border-b border-border/60">{nav}</div>
      )}

      {side ? (
        sidePosition === 'fixed' ? (
          // Below lg the rail is pinned to the bottom of the viewport as a
          // compact, scrollable drawer (notch-safe) so it never pushes the body
          // down; the body reserves space for it. On lg+ it returns to the
          // sticky right column.
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
            <div className="pb-[40vh] lg:pb-0">{children}</div>
            <aside
              className={cn(
                'fixed inset-x-0 bottom-0 z-30 max-h-[40vh] overflow-auto space-y-3',
                'border-t border-border/60 bg-surface px-4 pt-3',
                'pb-[max(0.75rem,env(safe-area-inset-bottom))]',
                'lg:static lg:z-auto lg:max-h-none lg:overflow-visible lg:border-0 lg:bg-transparent lg:p-0',
                'lg:sticky lg:top-4 lg:self-start',
              )}
            >
              {side}
            </aside>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
            <div>{children}</div>
            <aside
              className={cn(
                'space-y-3 lg:sticky lg:top-4 lg:self-start',
                // `sticky` floats the rail's top while the body scrolls, even
                // below lg — vs. `stack`, which lets it scroll away with the page.
                sidePosition === 'sticky' &&
                  'sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-auto',
              )}
            >
              {side}
            </aside>
          </div>
        )
      ) : (
        children
      )}
    </div>
  );
}

WorkbenchShell.displayName = 'WorkbenchShell';
